import { Button, Divider, Empty, Modal, Row, Space, Spin, Tooltip } from "antd";
import styles from "./index.less";
import { useState } from "react";
import { IMessage } from "@/services/message";
import EmptyIcon from "@/assets/images/notification_empty.webp";
import { useIntl } from "@umijs/max";
import { ReactComponent as QuestionCircleOutlined } from "@/assets/images/question-circle-outlined.svg";
import { ReactComponent as RightArrow } from "@/assets/images/right-arrow-small.svg";
import { routerPath } from "@/constants";
import { LoadingOutlined } from "@ant-design/icons";

type DetailModalProps = {
  data: IMessage;
  onConfirm: () => void;
  onClose: () => void;
  open: boolean;
};
const DetailModal = (props: DetailModalProps) => {
  const { open, data, onClose, onConfirm } = props;
  const intl = useIntl();

  return (
    <Modal
      title={
        data?.title || intl.formatMessage({ id: "menu.messages.detail.title" })
      }
      footer={false}
      open={open}
      onCancel={onClose}
    >
      <div className="mb-4">{data?.content}</div>
      <Row justify="center">
        <Space>
          <Button onClick={onClose}>
            {intl.formatMessage({ id: "menu.messages.detail.action.close" })}
          </Button>
          <Button type="primary" onClick={onConfirm}>
            {intl.formatMessage({ id: "menu.messages.detail.action.read" })}
          </Button>
        </Space>
      </Row>
    </Modal>
  );
};

const MessageItem = (props: { onClick: () => void; data: IMessage }) => {
  const { onClick, data } = props;
  return (
    <div className={styles.item} onClick={onClick}>
      <div className={styles["item-title"]}>{data.title}</div>
      <div
        className={styles.content}
        dangerouslySetInnerHTML={{ __html: data.content }}
      />
      <div className={styles.time}>{data.messageTime}</div>
    </div>
  );
};

export type NotificationProps = {
  data?: IMessage[];
  loading: boolean;
  onRead: (msg?: IMessage) => Promise<boolean>;
  onlyShowList?: boolean;
  listClassName?: string;
};
export default function Notification(props: NotificationProps) {
  const { data, onRead, loading, onlyShowList, listClassName } = props;
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<IMessage>();
  const intl = useIntl();

  return (
    <div className={styles.card}>
      {!onlyShowList && (
        <>
          <Row className={"w-full"} justify="space-between" align="middle">
            <div className={styles.title}>
              {intl.formatMessage({ id: "menu.messages.title" })}
              <Tooltip
                title={intl.formatMessage({ id: "menu.messages.title.tip" })}
              >
                <QuestionCircleOutlined className={"ml-1 cursor-help"} />
              </Tooltip>
            </div>

            <Button
              type="link"
              className="text-[12px] pr-0"
              onClick={() => onRead()}
            >
              {intl.formatMessage({ id: "menu.messages.readAll" })}
            </Button>
          </Row>
          <Divider className="my-2" />
        </>
      )}

      <div className={`${styles.list} ${listClassName} hide-scrollbar`}>
        <Spin
          spinning={!data?.length && loading}
          indicator={<LoadingOutlined />}
        >
          {!data?.length && (
            <Empty
              description={null}
              image={
                <img
                  src={EmptyIcon}
                  alt="empty"
                  className="w-[166px] h-[166px]"
                />
              }
              style={{ display: "flex", justifyContent: "center" }}
              className={styles.empty}
            />
          )}
          {data?.map((item) => (
            <MessageItem
              onClick={() => {
                setOpen(true);
                setMsg(item);
              }}
              key={item.id}
              data={item}
            />
          ))}
        </Spin>
      </div>
      {!onlyShowList && (
        <>
          <Divider className="my-2" />
          <div className={styles["go-btn"]}>
            <a
              onClick={(e) => {
                e.preventDefault();
                window.open(routerPath.NOTIFICATION, "_blank");
              }}
              className="flex items-center justify-center text-primary-600"
            >
              {intl.formatMessage({ id: "menu.message.redirect-notification" })}
              <RightArrow className="inline" />
            </a>
          </div>
        </>
      )}

      <DetailModal
        open={open}
        data={msg!}
        onClose={() => {
          setMsg(undefined);
          setOpen(false);
        }}
        onConfirm={async () => {
          const ok = await onRead(msg!);
          if (ok) {
            setOpen(false);
            setMsg(undefined);
          }
        }}
      />
    </div>
  );
}
